import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../AppPaths';
import { getFiGrowthBook, useIsGrowthBookEnabled } from './growthbook';
import {
  DEFAULT_QUIZ_VARIANT,
  HARDWARE_FIRST_VARIANT,
  LEGACY_VARIANT,
  parseQuizVariantOverride,
} from './fiQuizOverride';
import { hasQuizAutoOpenParam, removeQuizAutoOpenParam } from './quizDeepLink';
import useHardwareFirstShop from '../hooks/useHardwareFirstShop';
import { useHardwareFirstQuiz } from '../components/HardwareFirstQuiz/HardwareFirstQuizContext';

// Persists a querystring override so it survives client-side navigation for the
// rest of the session (matches how the email popup keeps its force state).
const QUIZ_VARIANT_STORAGE_KEY = 'fi_quiz_variant_override';

declare global {
  interface Window {
    // Exposed by the embedded fi-quizz widget (quiz.fitracking.com/widget.js).
    FiQuiz?: {
      open: (source?: string) => void;
      close: () => void;
    };
    // Host contract: the widget fails closed unless we set `enabled: true`.
    // `variant` names the design the widget should render (see fi-quizz).
    // `autoOpen` (a trigger name) tells a widget that hasn't loaded yet to open
    // as soon as it initializes — so a page that lands with the quiz open
    // doesn't have to race widget.js. Read once, at the widget's init.
    FiQuizConfig?: {
      enabled?: boolean;
      variant?: string;
      autoOpen?: boolean | string;
      // `hardwareFirst` points the widget's result CTAs at `/shop?device=`, the single page
      // that holds every tracker. The widget cannot evaluate the flag itself - it also runs
      // on marketing hosts - so the host that opened it decides.
      hardwareFirst?: boolean;
    };
  }
}

/** `quiz_opened` trigger for the quiz reached by URL (`/quiz`), vs a CTA click. */
export const QUIZ_URL_TRIGGER = 'ecom_quiz_url';

/**
 * Reads the quiz-variant override from the current URL, persisting it to
 * sessionStorage so it survives SPA navigation, and falling back to any
 * previously-stored value. Safe to call when the URL / sessionStorage are
 * unavailable (privacy mode, non-browser env).
 */
function getStickyQuizVariantOverride(): string | undefined {
  try {
    const fromUrl = parseQuizVariantOverride(window.location.search);
    if (fromUrl) {
      window.sessionStorage?.setItem(QUIZ_VARIANT_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    return window.sessionStorage?.getItem(QUIZ_VARIANT_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export interface QuizExperienceChoice {
  /** Open the fi-quizz widget; otherwise use the legacy in-app quiz. */
  openWidget: boolean;
  /** Widget design variant to render (meaningless when `openWidget` is false). */
  variant: string;
}

/**
 * Which quiz this visitor gets. `'legacy'` forces the old in-app quiz; any
 * other `?quizVariant=` value forces the widget with that design variant; with
 * no override the `ultra-experience` flag decides (default variant).
 *
 * Shared by the CTA launcher and the `/quiz` route so a URL visit and a click
 * can't land on different quizzes.
 */
function resolveQuizExperience(growthBookReady: boolean): QuizExperienceChoice {
  const override = getStickyQuizVariantOverride();
  const flagEnabled = growthBookReady && getFiGrowthBook().getFeatureValue<boolean>('ultra-experience', false);

  return {
    openWidget: override ? override !== LEGACY_VARIANT : flagEnabled,
    variant: override && override !== LEGACY_VARIANT ? override : DEFAULT_QUIZ_VARIANT,
  };
}

/** Render-time form of `resolveQuizExperience` (see `QuizRoute`). */
export function useQuizExperience(): QuizExperienceChoice {
  return resolveQuizExperience(useIsGrowthBookEnabled());
}

/**
 * Opens the fi-quizz widget, affirming its fails-closed host contract.
 *
 * `autoOpen` is set as well as calling `open()` because the widget script is
 * `defer`red: on a cold load of a page that opens the quiz on arrival, the
 * widget may not exist yet, and it will pick the intent up at its own init.
 * Whichever runs last opens the quiz; a second open while it's on screen is a
 * no-op in the widget.
 */
export function openQuizWidget(trigger: string, variant: string, hardwareFirst = false): void {
  window.FiQuizConfig = { enabled: true, variant, autoOpen: trigger, hardwareFirst };
  window.FiQuiz?.open(trigger);
}

/**
 * Consumes `?openQuiz=1` on the PLP: the landing half of the `/quiz` redirect
 * (see `QuizRoute` and quizDeepLink.ts). Opens the widget once and drops the
 * param so a refresh or a back-navigation doesn't reopen it.
 *
 * A stale link that arrives while the widget path is off (flag off,
 * `?quizVariant=legacy`) is sent to `/quiz`, which renders the legacy quiz —
 * a link to the quiz always reaches a quiz.
 */
export function useAutoOpenQuizFromUrl(): void {
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { openWidget, variant } = useQuizExperience();
  const hardwareFirst = useHardwareFirstShop();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current || !hasQuizAutoOpenParam(search)) {
      return;
    }
    handledRef.current = true;

    const cleanedSearch = removeQuizAutoOpenParam(search);
    if (!openWidget) {
      history.replace({ pathname: AppPaths.Quiz, search: cleanedSearch });
      return;
    }

    history.replace({ pathname, search: cleanedSearch });
    openQuizWidget(QUIZ_URL_TRIGGER, variant, hardwareFirst);
  }, [pathname, search, openWidget, variant, hardwareFirst, history]);
}

/**
 * Returns a handler that launches the product-recommendation quiz.
 *
 * A `?quizVariant=` (or `?forceShowQuiz`) override wins over the flag so either
 * quiz — and any named widget design variant — can be exercised in prod before
 * Ultra GA. `?quizVariant=legacy` forces the legacy quiz; any other override
 * value forces the widget with that design variant. Otherwise, when the
 * `ultra-experience` flag is on (i.e. Fi Ultra is GA) and the embedded fi-quizz
 * widget has loaded, it opens the new widget (default variant) — which
 * recommends Mini / Series 3+ / Ultra. In every other case it falls back to the
 * legacy in-app quiz at `AppPaths.Quiz`, so there is always a working quiz.
 *
 * Deliberately NOT gated on `ultra-discoverable`: the quiz stays available
 * when Ultra entry points are hidden, and an Ultra recommendation from it is
 * acceptable (product decision).
 */
export function useLaunchQuiz(): () => void {
  const history = useHistory();
  const growthBookReady = useIsGrowthBookEnabled();
  const hardwareFirst = useHardwareFirstShop();
  const inAppQuiz = useHardwareFirstQuiz();

  return () => {
    // Resolved at click time, not render time, so a `?quizVariant=` picked up
    // mid-session takes effect without a remount.
    const override = getStickyQuizVariantOverride();
    const { openWidget, variant } = resolveQuizExperience(growthBookReady);

    // The arm's own sheet wins over the widget: it is the only quiz whose result
    // acts on the shop's device selection instead of navigating away from it.
    // `?quizVariant=legacy` still forces the legacy quiz, so there is always an
    // escape hatch to the old flow.
    if (override !== LEGACY_VARIANT && (override === HARDWARE_FIRST_VARIANT || hardwareFirst)) {
      inAppQuiz.open('ecom_plp');
      return;
    }

    if (openWidget && window.FiQuiz) {
      openQuizWidget('ecom_plp', variant, hardwareFirst);
      return;
    }

    history.push(AppPaths.Quiz);
  };
}
